import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, 
  GraduationCap, 
  Calendar, 
  TrendingUp, 
  AlertTriangle,
  CheckCircle,
  Clock
} from 'lucide-react';
import Layout from '../components/Layout';
import { useApp } from '../contexts/AppContext';
import { format, startOfWeek, endOfWeek, isToday } from 'date-fns';

const Dashboard: React.FC = () => {
  const { students, classes, attendanceRecords, selectedClass, selectedDate } = useApp();
  const [stats, setStats] = useState({
    totalStudents: 0,
    totalClasses: 0,
    todayAttendance: 0,
    attendanceRate: 0,
    behaviorIncidents: 0,
    needsAttention: 0
  });

  useEffect(() => {
    const calculateStats = () => {
      const today = format(new Date(), 'yyyy-MM-dd');
      const todayRecords = attendanceRecords.filter(record => record.date === today);
      
      const presentToday = todayRecords.filter(record => record.status === 'present').length;
      const totalToday = todayRecords.length;
      const attendanceRate = totalToday > 0 ? (presentToday / totalToday) * 100 : 0;
      
      const behaviorIncidents = todayRecords.reduce((sum, record) => 
        sum + (record.behaviorIncidents?.length || 0), 0
      );
      
      const needsAttention = students.filter(student => {
        const studentRecord = todayRecords.find(r => r.studentId === student.id);
        return studentRecord && (studentRecord.status === 'absent' || 
          (studentRecord.behaviorIncidents?.length || 0) > 0);
      }).length;

      setStats({
        totalStudents: students.length,
        totalClasses: classes.length,
        todayAttendance: presentToday,
        attendanceRate: Math.round(attendanceRate),
        behaviorIncidents,
        needsAttention
      });
    };

    calculateStats();
  }, [students, classes, attendanceRecords]);

  const getWeekRange = () => {
    const now = new Date();
    const start = startOfWeek(now, { weekStartsOn: 1 });
    const end = endOfWeek(now, { weekStartsOn: 1 });
    return `${format(start, 'MMM d')} - ${format(end, 'MMM d')}`;
  };

  const recentActivities = [
    {
      id: 1,
      type: 'attendance',
      message: `${stats.todayAttendance} students marked present today`,
      time: '2 minutes ago',
      icon: CheckCircle,
      color: 'text-green-600'
    },
    {
      id: 2,
      type: 'behavior',
      message: `${stats.behaviorIncidents} behavior incidents reported`,
      time: '1 hour ago',
      icon: AlertTriangle,
      color: 'text-yellow-600'
    },
    {
      id: 3,
      type: 'class',
      message: `${classes.length} classes configured`,
      time: '2 hours ago',
      icon: GraduationCap,
      color: 'text-blue-600'
    }
  ];

  return (
    <Layout>
      <div className="p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-300">
            Welcome back! Here's what's happening with your classes today.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Users className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Students</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.totalStudents}</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <GraduationCap className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Classes</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.totalClasses}</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Calendar className="h-8 w-8 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Today's Attendance</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.todayAttendance}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{stats.attendanceRate}% rate</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-8 w-8 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Needs Attention</p>
                <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.needsAttention}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{stats.behaviorIncidents} incidents</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Quick Actions */}
          <div className="card">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <Link
                to="/attendance"
                className="flex items-center p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <Calendar className="h-5 w-5 text-primary-600 mr-3" />
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Mark Today's Attendance</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Record student attendance for today</p>
                </div>
              </Link>
              
              <Link
                to="/students"
                className="flex items-center p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <Users className="h-5 w-5 text-primary-600 mr-3" />
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Manage Students</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Add, edit, or view student profiles</p>
                </div>
              </Link>
              
              <Link
                to="/reports"
                className="flex items-center p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <TrendingUp className="h-5 w-5 text-primary-600 mr-3" />
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">View Reports</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Generate attendance and behavior reports</p>
                </div>
              </Link>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="card">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Recent Activity</h3>
            <div className="space-y-4">
              {recentActivities.map((activity) => (
                <div key={activity.id} className="flex items-start">
                  <div className={`flex-shrink-0 ${activity.color}`}>
                    <activity.icon className="h-5 w-5" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-900 dark:text-white">{activity.message}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{activity.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Current Class Info */}
        {selectedClass && (
          <div className="mt-8 card">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Current Class</h3>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  {classes.find(c => c.id === selectedClass)?.name || 'Unknown Class'}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {isToday(new Date(selectedDate)) ? 'Today' : format(new Date(selectedDate), 'MMM d, yyyy')}
                </p>
              </div>
              <Link
                to="/attendance"
                className="btn-primary"
              >
                View Attendance
              </Link>
            </div>
          </div>
        )}

        {/* Week Overview */}
        <div className="mt-8 card">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">This Week ({getWeekRange()})</h3>
          <div className="grid grid-cols-7 gap-2">
            {Array.from({ length: 7 }, (_, i) => {
              const date = new Date();
              date.setDate(date.getDate() - date.getDay() + i + 1);
              const dateStr = format(date, 'yyyy-MM-dd');
              const isTodayDate = isToday(date);
              const dayRecords = attendanceRecords.filter(r => r.date === dateStr);
              const presentCount = dayRecords.filter(r => r.status === 'present').length;
              const totalCount = dayRecords.length;
              
              return (
                <div
                  key={i}
                  className={`p-3 rounded-lg text-center ${
                    isTodayDate 
                      ? 'bg-primary-100 dark:bg-primary-900 border-2 border-primary-500 dark:border-primary-400' 
                      : 'bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600'
                  }`}
                >
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    {format(date, 'EEE')}
                  </p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">
                    {format(date, 'd')}
                  </p>
                  {totalCount > 0 && (
                    <p className="text-xs text-gray-600 dark:text-gray-300">
                      {presentCount}/{totalCount}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
